import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import httpClient from "../../utils/httpClients";
import { usePlayer } from "../../../PlayerContext";

function Artist() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [genres, setGenres] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [likedSongs, setLikedSongs] = useState(new Set());
  const [sharePopup, setSharePopup] = useState({ open: false, song: null });
  const [isFollowed, setIsFollowed] = useState(false);

  const [playlists, setPlaylists] = useState([]);
  const [playlistModal, setPlaylistModal] = useState({
    open: false,
    song: null,
  });
  const [selectedPlaylists, setSelectedPlaylists] = useState(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPlaylist, setNewPlaylist] = useState({
    name: "",
    description: "",
    coverImage: null,
  });
  const [playlistLoading, setPlaylistLoading] = useState(false);

  const sliderRef = useRef(null);

  const {
    isPlaying,
    currentTrack,
    seek,
    duration,
    triggerQueueUpdate,
    tracks,
    togglePlayPause,
  } = usePlayer();

  // === FETCH GENRES ===
  const fetchGenresSongs = async () => {
    try {
      setLoading(true);
      const res = await httpClient.get(`/user/music/get-themes`);
      if (res.data?.success) {
        setGenres(res.data.data.getThemes || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGenresSongs();
  }, []);

  // === QUEUE ACTION (supports genre + normal tracks) ===
  const handleQueueAction = async (action, id, type = null) => {
    try {
      const payload = { action };
      if (type === "genresong") {
        payload.playlistId = id;
        payload.type = "genresong";
      } else if (action === "addAllToQueue" && Array.isArray(id)) {
        payload.trackId = id;
      } else {
        payload.trackId = id;
      }
      const res = await httpClient.post(
        "user/queue/queue-controller",
        payload,
        { skipLoader: true, skipErrorToast: true },
      );
      if (res.data?.success) {
        triggerQueueUpdate();
        return true;
      }
    } catch (err) {
      console.error("Queue error:", err);
      return false;
    }
  };

  // === FORMAT DURATION ===
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // === FETCH ARTIST DATA ===
  const fetchArtistSongs = async () => {
    try {
      setLoading(true);
      const res = await httpClient.get(`/user/music/go-to-artist/${id}`);
      if (res.data && res.data.success) {
        const data = res.data.data;
        setArtist(data.artist);
        setSongs(data.popularSongs || []);
        setIsFollowed(data.artist?.isFollow || false);

        const liked = new Set();
        data.popularSongs?.forEach(
          (song) => song.isLiked && liked.add(song._id),
        );
        setLikedSongs(liked);
      } else {
        setError("Failed to fetch artist data");
      }
    } catch (err) {
      setError("Error fetching artist data");
    } finally {
      setLoading(false);
    }
  };

  // === LIKE SONG === (optimistic update)
  const handleLikeSong = async (contentId, isCurrentlyLiked, e) => {
    e.preventDefault();
    const newLiked = !isCurrentlyLiked;

    const newSet = new Set(likedSongs);
    isCurrentlyLiked ? newSet.delete(contentId) : newSet.add(contentId);
    setLikedSongs(newSet);
    setSongs((prev) =>
      prev.map((s) => (s._id === contentId ? { ...s, isLiked: newLiked } : s)),
    );

    try {
      const action = isCurrentlyLiked ? "dislike" : "like";
      const res = await httpClient.post(
        `/user/play-history/like-content/${contentId}/${action}`,
        {},
        { skipToast: true, skipLoader: true },
      );
      if (!res.data?.success) {
        const revertSet = new Set(likedSongs);
        setLikedSongs(revertSet);
        setSongs((prev) =>
          prev.map((s) =>
            s._id === contentId ? { ...s, isLiked: isCurrentlyLiked } : s,
          ),
        );
      }
    } catch (err) {
      console.error(err);
      const revertSet = new Set(likedSongs);
      setLikedSongs(revertSet);
      setSongs((prev) =>
        prev.map((s) =>
          s._id === contentId ? { ...s, isLiked: isCurrentlyLiked } : s,
        ),
      );
    }
  };

  // === FOLLOW ARTIST ===
  const handleFollowArtist = async (e) => {
    e.preventDefault();
    try {
      const action = isFollowed ? "dislike" : "like";
      const res = await httpClient.post(
        `/user/Artist-follow/like-unlike/${id}/${action}`,
      );
      if (res.data && res.data.success) {
        setIsFollowed(!isFollowed);
        setArtist((prev) => ({ ...prev, isFollowed: !isFollowed }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  // === SMART PLAY ===
  const playNow = async (trackId) => {
    try {
      const isSame = currentTrack?._id === trackId;
      if (isSame) {
        togglePlayPause();
        return;
      }
      const success = await handleQueueAction("addAndPlay", trackId);
      if (success && !isPlaying) togglePlayPause();
    } catch (err) {
      console.error(err);
    }
  };

  // === PLAY ALL ===
  const playAllSongs = async (e) => {
    e.preventDefault();
    if (songs.length === 0) return;
    const trackIds = songs.map((s) => s._id);
    const success = await handleQueueAction("addAllToQueue", trackIds);
    if (success) await playNow(songs[0]._id);
  };

  // === GENRE CLICK (Play + Navigate) ===
  const handleGenreClick = (genreId) => {
    handleQueueAction("addAllToQueue", genreId, "genresong");
    navigate(`/party-music/${genreId}`);
  };

  // === PLAYLISTS ===
  const fetchPlaylists = async () => {
    try {
      const res = await httpClient.get("/user/playlist/get-playlists");
      if (res.data && res.data.success) {
        setPlaylists(res.data.data.playlist || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const openPlaylistModal = (song, e) => {
    e.preventDefault();
    e.stopPropagation();
    setPlaylistModal({ open: true, song });
    setSelectedPlaylists(new Set());
    fetchPlaylists();
    setOpenDropdown(null);
  };

  const closePlaylistModal = () => {
    setPlaylistModal({ open: false, song: null });
    setShowCreateForm(false);
    setNewPlaylist({ name: "", description: "", coverImage: null });
    setSelectedPlaylists(new Set());
  };

  const togglePlaylistSelection = (playlistId) => {
    const newSelected = new Set(selectedPlaylists);
    if (newSelected.has(playlistId)) {
      newSelected.delete(playlistId);
    } else {
      newSelected.add(playlistId);
    }
    setSelectedPlaylists(newSelected);
  };

  const handleAddToSelectedPlaylists = async () => {
    if (selectedPlaylists.size === 0) {
      alert("Please select at least one playlist");
      return;
    }
    setPlaylistLoading(true);
    try {
      for (const pid of selectedPlaylists) {
        const params = new URLSearchParams({
          playlistId: pid,
          itemId: playlistModal.song._id,
          itemType: "Music",
        });
        await httpClient.post(`/user/playlist/add-item?${params}`);
      }
      closePlaylistModal();
    } catch (err) {
      console.error(err);
      alert("Failed to add to some playlists");
    } finally {
      setPlaylistLoading(false);
    }
  };

  const createNewPlaylist = async () => {
    if (!newPlaylist.name.trim()) {
      alert("Playlist name is required");
      return;
    }
    setPlaylistLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", newPlaylist.name);
      formData.append("description", newPlaylist.description);
      if (newPlaylist.coverImage)
        formData.append("coverImage", newPlaylist.coverImage);

      const res = await httpClient.post(
        "/user/playlist/create-playlist",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (res.data && res.data.success) {
        await fetchPlaylists();
        setShowCreateForm(false);
        setNewPlaylist({ name: "", description: "", coverImage: null });
        if (playlistModal.song) {
          await addToPlaylist(
            res.data.data.playlist._id,
            playlistModal.song._id,
          );
        }
      }
    } catch (err) {
      alert("Failed to create playlist");
    } finally {
      setPlaylistLoading(false);
    }
  };

  const addToPlaylist = async (playlistId, songId) => {
    const params = new URLSearchParams({
      playlistId,
      itemId: songId,
      itemType: "Music",
    });
    await httpClient.post(`/user/playlist/add-item?${params}`);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setNewPlaylist((prev) => ({ ...prev, coverImage: file }));
  };

  // === SHARE ===
  const openSharePopup = (song, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSharePopup({ open: true, song });
  };

  const openArtistSharePopup = () => {
    setSharePopup({
      open: true,
      song: { _id: id, title: artist.artist, isArtist: true },
    });
  };

  const closeSharePopup = () => setSharePopup({ open: false, song: null });

  const handleCopy = async () => {
    const text = sharePopup.song.isArtist
      ? `${window.location.origin}/artist/${id}`
      : `${window.location.origin}/music/${encodeURIComponent(sharePopup.song._id)}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
  };

  const shareOnSocialMedia = (platform) => {
    const song = sharePopup.song;
    const url = song.isArtist
      ? `${window.location.origin}/artist/${id}`
      : `${window.location.origin}/music/${encodeURIComponent(song._id)}`;
    const text = encodeURIComponent(
      song.isArtist ? `Check out ${artist.artist} on VolumeTV` : song.title,
    );

    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      whatsapp: `https://api.whatsapp.com/send?text=${text}%20${url}`,
      telegram: `https://t.me/share/url?url=${url}&text=${text}`,
    };
    window.open(links[platform], "_blank", "width=600,height=400");
  };

  // === DROPDOWN TOGGLE ===
  const toggleDropdown = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDropdown(openDropdown === key ? null : key);
  };

  useEffect(() => {
    const close = (e) => {
      if (
        !e.target.closest(".more-dropdown") &&
        !e.target.closest(".dropdown")
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (id) fetchArtistSongs();
  }, [id]);

  // === LOADING / ERROR ===
  if (loading)
    return (
      <div
        className="content-inner pb-0 container-fluid d-flex justify-content-center align-items-center"
        style={{ height: "70vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );

  if (error)
    return <div className="alert alert-danger text-center my-5">{error}</div>;
  if (!artist)
    return (
      <div className="alert alert-warning text-center my-5">
        Artist not found
      </div>
    );

  const totalMinutes = Math.floor(
    songs.reduce((a, s) => a + (s.duration || 0), 0) / 60,
  );
  const totalTracks = songs.length;

  const sliderSettings = {
    dots: false,
    infinite: genres.length > 6,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 3,
    arrows: false,
    responsive: [
      { breakpoint: 1400, settings: { slidesToShow: 5 } },
      { breakpoint: 1200, settings: { slidesToShow: 4 } },
      { breakpoint: 992, settings: { slidesToShow: 3 } },
      { breakpoint: 768, settings: { slidesToShow: 2 } },
      { breakpoint: 576, settings: { slidesToShow: 1 } },
    ],
  };

  const CustomNextArrow = () => (
    <div
      className="swiper-button-next"
      onClick={() => sliderRef.current?.slickNext()}
      style={{ cursor: "pointer", zIndex: 10 }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="19"
        viewBox="0 0 18 19"
        fill="none"
      >
        <path
          d="M15.5855 9.89797L10.523 14.9605C10.4174 15.066 10.2743 15.1253 10.125 15.1253C9.97573 15.1253 9.83258 15.066 9.72703 14.9605C9.62148 14.8549 9.56219 14.7118 9.56219 14.5625C9.56219 14.4132 9.62148 14.2701 9.72703 14.1645L13.8298 10.0625H2.8125C2.66332 10.0625 2.52024 10.0032 2.41475 9.89775C2.30926 9.79226 2.25 9.64918 2.25 9.5C2.25 9.35082 2.30926 9.20774 2.41475 9.10225C2.52024 8.99676 2.66332 8.9375 2.8125 8.9375H13.8298L9.72703 4.83547C9.62148 4.72992 9.56219 4.58677 9.56219 4.4375C9.56219 4.28823 9.62148 4.14508 9.72703 4.03953C9.83258 3.93398 9.97573 3.87469 10.125 3.87469C10.2743 3.87469 10.4174 3.93398 10.523 4.03953L15.5855 9.10203C15.6378 9.15427 15.6793 9.21631 15.7076 9.2846C15.7359 9.35288 15.7504 9.42608 15.7504 9.5C15.7504 9.57392 15.7359 9.64712 15.7076 9.7154C15.6793 9.78369 15.6378 9.84573 15.5855 9.89797Z"
          fill="currentColor"
        />
      </svg>
    </div>
  );

  const CustomPrevArrow = () => (
    <div
      className="swiper-button-prev"
      onClick={() => sliderRef.current?.slickPrev()}
      style={{ cursor: "pointer", zIndex: 10 }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="19"
        viewBox="0 0 18 19"
        fill="none"
      >
        <path
          d="M15.75 9.5C15.75 9.64918 15.6908 9.79226 15.5853 9.89775C15.4798 10.0032 15.3367 10.0625 15.1875 10.0625H4.17025L8.27298 14.1645C8.32525 14.2168 8.3667 14.2788 8.39499 14.3471C8.42327 14.4154 8.43783 14.4886 8.43783 14.5625C8.43783 14.6364 8.42327 14.7096 8.39499 14.7779C8.3667 14.8462 8.32525 14.9082 8.27298 14.9605C8.22072 15.0127 8.15868 15.0542 8.09039 15.0825C8.02211 15.1108 7.94893 15.1253 7.87502 15.1253C7.80111 15.1253 7.72792 15.1108 7.65964 15.0825C7.59135 15.0542 7.52931 15.0127 7.47705 14.9605L2.41455 9.89797C2.36225 9.84573 2.32076 9.78369 2.29245 9.7154C2.26414 9.64712 2.24957 9.57392 2.24957 9.5C2.24957 9.42608 2.26414 9.35288 2.29245 9.2846C2.32076 9.21631 2.36225 9.15427 2.41455 9.10203L7.47705 4.03953C7.58259 3.93398 7.72575 3.87469 7.87502 3.87469C8.02428 3.87469 8.16744 3.93398 8.27298 4.03953C8.37853 4.14508 8.43783 4.28823 8.43783 4.4375C8.43783 4.58677 8.37853 4.72992 8.27298 4.83547L4.17025 8.9375H15.1875C15.3367 8.9375 15.4798 8.99676 15.5853 9.10225C15.6908 9.20774 15.75 9.35082 15.75 9.5Z"
          fill="#4A525F"
        />
      </svg>
    </div>
  );

  return (
    <div className="content-inner pb-0 container-fluid" id="page_layout">
      <div className="row row--grid">
        {/* Breadcrumb */}
        <div className="col-12 mb-3">
          <ul className="breadcrumb">
            <li className="breadcrumb__item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb__item breadcrumb__item--active">
              {artist.artist}
            </li>
          </ul>
        </div>

        {/* Artist Header */}
        <div className="col-12 mb-4">
          <div className="release">
            <div className="release__content trending-song d-flex flex-column flex-md-row align-items-center gap-4">
              <div className="release__cover">
                <img
                  src={
                    artist.coverImageUrl ||
                    `${process.env.PUBLIC_URL}/assets/images/dashboard/userLogo.jpeg`
                  }
                  alt={artist.artist}
                  className="img-fluid rounded-circle"
                  style={{
                    width: "180px",
                    height: "180px",
                    objectFit: "cover",
                  }}
                />
              </div>
              <div className="trending-song-detail flex-grow-1">
                <h1 className="display-5 fw-bold mb-2">{artist.artist}</h1>
                <div className="release__stat mb-3">
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="18"
                      fill="currentColor"
                    >
                      <path d="M21.65,2.24a1,1,0,0,0-.8-.23l-13,2A1,1,0,0,0,7,5V15.35A3.45,3.45,0,0,0,5.5,15,3.5,3.5,0,1,0,9,18.5V10.86L20,9.17v4.18A3.45,3.45,0,0,0,18.5,13,3.5,3.5,0,1,0,22,16.5V3A1,1,0,0,0,21.65,2.24ZM5.5,20A1.5,1.5,0,1,1,7,18.5,1.5,1.5,0,0,1,5.5,20Zm13-2A1.5,1.5,0,1,1,20,16.5,1.5,1.5,0,0,1,18.5,18ZM20,7.14,9,8.83v-3L20,4.17Z" />
                    </svg>{" "}
                    {totalTracks} track{totalTracks !== 1 ? "s" : ""} •{" "}
                    {totalMinutes} min
                  </span>
                </div>

                {/* YOUR EXACT BUTTONS */}
                <div className="d-flex gap-3 align-items-center flex-wrap mt-4">
                  <a
                    href="#!"
                    className="release__buy open-modal d-flex align-items-center gap-2"
                    onClick={playAllSongs}
                    style={{
                      padding: "14px 36px",
                      fontSize: "16px",
                      fontWeight: "600",
                      borderRadius: "50px",
                      backgroundColor: "#1DB954",
                      color: "white",
                      transition: "all 0.3s ease",
                      opacity: songs.length === 0 ? 0.6 : 1,
                      pointerEvents: songs.length === 0 ? "none" : "auto",
                    }}
                  >
                    <i className="fas fa-play me-2"></i> Play All
                  </a>

                  <a
                    href="#!"
                    className={`release__buy open-modal ${isFollowed ? "liked" : ""}`}
                    onClick={handleFollowArtist}
                    style={{
                      padding: "12px 24px",
                      fontSize: "15px",
                      border: "1px solid #444",
                      borderRadius: "50px",
                      backgroundColor: isFollowed ? "#1DB954" : "transparent",
                      color: "white",
                    }}
                  >
                    <i
                      className={isFollowed ? "fas fa-heart" : "far fa-heart"}
                    ></i>{" "}
                    {isFollowed ? "Following" : "Follow"}
                  </a>

                  <div className="more-dropdown position-relative">
                    <a
                      href="#!"
                      className="release__buy open-modal more"
                      onClick={(e) => toggleDropdown("artist-more", e)}
                      style={{
                        padding: "10px 20px",
                        fontSize: "15px",
                        border: "1px solid #444",
                        borderRadius: "50px",
                        backgroundColor: "transparent",
                        color: "white",
                      }}
                    >
                      <i className="fa-solid fa-ellipsis"></i> More
                    </a>

                    {openDropdown === "artist-more" && (
                      <div
                        className="dropdown-menu show"
                        style={{
                          position: "absolute",
                          top: "100%",
                          right: 0,
                          zIndex: 9999,
                          background: "#2a2a2a",
                          border: "1px solid #444",
                          borderRadius: "12px",
                          minWidth: "210px",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.6)",
                          marginTop: "8px",
                          padding: "8px 0",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <a
                          href="#!"
                          className="dropdown-item d-flex align-items-center gap-3"
                          onClick={(e) => {
                            e.preventDefault();
                            openArtistSharePopup();
                            setOpenDropdown(null);
                          }}
                          style={{ padding: "10px 16px", color: "white" }}
                        >
                          <span
                            className="material-icons"
                            style={{ fontSize: "20px" }}
                          >
                            share
                          </span>
                          Share
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Songs List */}
        <div className="col-12">
          <div className="release">
            <div className="release__list trending">
              <ul className="main__list main__list--playlist main__list--dashbox p-0">
                <h4 className="card-title text-capitalize episodes mb-3">
                  Popular Songs ({songs.length})
                </h4>

                {songs.length > 0 ? (
                  songs.map((song, index) => {
                    const isPlayingNow = currentTrack?._id === song._id;
                    const isLiked = likedSongs.has(song._id);
                    return (
                      <li
                        key={song._id}
                        className={`single-item list-1 d-flex align-items-center py-3 px-2 rounded-3 mb-2 ${isPlayingNow ? "active-episode" : ""}`}
                        style={{
                          backgroundColor: isPlayingNow
                            ? "rgba(29, 185, 84, 0.15)"
                            : "transparent",
                          borderLeft: isPlayingNow
                            ? "4px solid #1DB954"
                            : "4px solid transparent",
                        }}
                      >
                        <div
                          className="single-item__cover me-3"
                          onClick={() => playNow(song._id)}
                          style={{ cursor: "pointer", flex: "0 0 60px" }}
                        >
                          <img
                            src={song.coverImageUrl || "img/covers/cover.svg"}
                            alt=""
                            className="img-fluid rounded"
                          />
                          {isPlayingNow && isPlaying ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="#1DB954"
                            >
                              <path d="M16,2a3,3,0,0,0-3,3V19a3,3,0,0,0,6,0V5A3,3,0,0,0,16,2Zm1,17a1,1,0,0,1-2,0V5a1,1,0,0,1,2,0ZM8,2A3,3,0,0,0,5,5V19a3,3,0,0,0,6,0V5A3,3,0,0,0,8,2ZM9,19a1,1,0,0,1-2,0V5A1,1,0,0,1,9,5Z" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill={isPlayingNow ? "#1DB954" : "#FFF"}
                            >
                              <path d="M18.54,9,8.88,3.46a3.42,3.42,0,0,0-5.13,3V17.58A3.42,3.42,0,0,0,7.17,21a3.43,3.43,0,0,0,1.71-.46L18.54,15a3.42,3.42,0,0,0,0-5.92Zm-1,4.19L7.88,18.81a1.44,1.44,0,0,1-1.42,0,1.42,1.42,0,0,1-.71-1.23V6.42a1.42,1.42,0,0,1,.71-1.23A1.51,1.51,0,0,1,7.17,5a1.54,1.54,0,0,1,.71.19l9.66,5.58a1.42,1.42,0,0,1,0,2.46Z" />
                            </svg>
                          )}
                        </div>
                        <div className="single-item__title flex-grow-1">
                          <h4
                            style={{
                              margin: 0,
                              fontSize: "15px",
                              cursor:
                                song.isAlbum && song.parentId
                                  ? "pointer"
                                  : "default",
                            }}
                            onClick={() =>
                              song.isAlbum &&
                              song.parentId &&
                              navigate(`/album/${song.parentId}`)
                            }
                          >
                            {index + 1}. {song.title}
                            {isPlayingNow && (
                              <span className="badge bg-success ms-2">
                                Playing
                              </span>
                            )}
                          </h4>
                          <span className="text-muted small">
                            {song.artist || "Unknown Artist"}
                          </span>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <a
                            href="#!"
                            className={`single-item__add ${isLiked ? "liked" : ""}`}
                            onClick={(e) =>
                              handleLikeSong(song._id, isLiked, e)
                            }
                          >
                            <i
                              className={
                                isLiked ? "fas fa-heart" : "far fa-heart"
                              }
                            ></i>
                          </a>

                          <div className="dropdown">
                            <span
                              className="text-white"
                              onClick={(e) => toggleDropdown(song._id, e)}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fas fa-ellipsis-h"></i>
                            </span>
                            {openDropdown === song._id && (
                              <div
                                className="dropdown-menu show"
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  right: 0,
                                  background: "#2a2a2a",
                                  border: "1px solid #444",
                                  borderRadius: "8px",
                                  minWidth: "180px",
                                  zIndex: 1000,
                                  padding: "8px 0",
                                }}
                              >
                                <a
                                  href="#!"
                                  className="dropdown-item d-flex align-items-center gap-2"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleQueueAction("add", song._id);
                                    setOpenDropdown(null);
                                  }}
                                >
                                  <span className="material-icons">
                                    playlist_add
                                  </span>{" "}
                                  Add to Queue
                                </a>
                                <a
                                  href="#!"
                                  className="dropdown-item d-flex align-items-center gap-2"
                                  onClick={(e) => openPlaylistModal(song, e)}
                                >
                                  <span className="material-icons">
                                    library_music
                                  </span>{" "}
                                  Add to Playlist
                                </a>
                                <a
                                  href="#!"
                                  className="dropdown-item d-flex align-items-center gap-2"
                                  onClick={(e) => openSharePopup(song, e)}
                                >
                                  <span className="material-icons">share</span>{" "}
                                  Share
                                </a>
                              </div>
                            )}
                          </div>

                          <a
                            href="#!"
                            className="single-item__export"
                            onClick={(e) => openSharePopup(song, e)}
                          >
                            <i className="fas fa-share"></i>
                          </a>

                          <span className="single-item__time text-muted small">
                            {isPlayingNow
                              ? `${formatDuration(seek)} / ${formatDuration(duration)}`
                              : formatDuration(song.duration)}
                          </span>
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <li className="text-center py-5 text-muted">
                    No songs available
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* GENRES SLIDER */}
      <div className="row mt-5">
        <div className="col-12">
          <div className="card-header mb-3 d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Top song genres for you</h4>
            <div className="common-album d-lg-flex d-none gap-3">
              <CustomPrevArrow />
              <CustomNextArrow />
            </div>
          </div>
          <div className="position-relative">
            <Slider ref={sliderRef} {...sliderSettings}>
              {genres.map((genre) => (
                <div key={genre._id} className="px-3">
                  <div className="position-relative d-inline-block w-100">
                    {/* IMAGE CLICK → playlistId + type: genre */}
                    <div
                      onClick={() => handleGenreClick(genre._id)}
                      style={{ cursor: "pointer" }}
                    >
                      <img
                        src={genre.themeImageUrl || `${process.env.PUBLIC_URL}/img/covers/cover.svg`}
                        alt={genre.name}
                        className="img-fluid rounded-3 mb-3 w-100"
                        style={{
                          height: "180px",
                          objectFit: "cover",
                          transition: "all 0.3s ease",
                        }}
                      />

                      {/* HOVER PLAY BUTTON */}
                      <div
                        className="position-absolute top-50 start-50 translate-middle opacity-0"
                        style={{
                          transition: "all 0.3s ease",
                          pointerEvents: "none",
                          zIndex: 2,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = 1;
                          e.currentTarget.previousElementSibling.style.transform =
                            "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = 0;
                          e.currentTarget.previousElementSibling.style.transform =
                            "scale(1)";
                        }}
                      >
                        <button
                          className="btn btn-success rounded-circle shadow-lg d-flex align-items-center justify-content-center"
                          style={{
                            width: "60px",
                            height: "60px",
                            fontSize: "24px",
                            backgroundColor: "#1DB954",
                            border: "none",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenreClick(genre._id);
                          }}
                        >
                          Play
                        </button>
                      </div>

                      {/* DARK OVERLAY */}
                      <div
                        className="position-absolute inset-0 rounded-3 opacity-0"
                        style={{
                          background: "rgba(0,0,0,0.5)",
                          transition: "opacity 0.3s ease",
                        }}
                        onMouseEnter={(e) => (e.target.style.opacity = 1)}
                        onMouseLeave={(e) => (e.target.style.opacity = 0)}
                      ></div>
                    </div>

                    {/* TITLE CLICK → NAVIGATE ONLY */}
                    <Link
                      to={`/party-music/${genre._id}`}
                      className="text-decoration-none text-white d-block"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h6 className="mb-1 text-capitalize">{genre.name}</h6>
                    </Link>
                    <small className="text-muted d-block text-capitalize">
                      {genre.description}
                    </small>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>

      {/* SHARE MODAL */}
      {sharePopup.open && sharePopup.song && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#1a1a1a",
              padding: "30px",
              borderRadius: "16px",
              width: "90%",
              maxWidth: "380px",
              color: "white",
              border: "1px solid #333",
            }}
          >
            <h4 className="text-center mb-3">Share</h4>
            <p className="text-center text-muted mb-4">
              {sharePopup.song.isArtist ? artist.artist : sharePopup.song.title}
            </p>
            <div className="d-flex justify-content-center gap-4 mb-4">
              {["facebook", "twitter", "whatsapp", "telegram"].map((p) => (
                <button
                  key={p}
                  onClick={() => shareOnSocialMedia(p)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      background:
                        p === "facebook"
                          ? "#1877f2"
                          : p === "twitter"
                            ? "#1da1f2"
                            : p === "whatsapp"
                              ? "#25d366"
                              : "#0088cc",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className={`fab fa-${p === "twitter" ? "x-twitter" : p}`}
                    ></i>
                  </div>
                  <small>{p.charAt(0).toUpperCase() + p.slice(1)}</small>
                </button>
              ))}
            </div>
            <div className="d-flex gap-2">
              <input
                type="text"
                readOnly
                value={
                  sharePopup.song.isArtist
                    ? `${window.location.origin}/artist/${id}`
                    : `${window.location.origin}/music/${encodeURIComponent(sharePopup.song._id)}`
                }
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#2a2a2a",
                  border: "1px solid #444",
                  borderRadius: "8px",
                  color: "white",
                }}
              />
              <button
                onClick={handleCopy}
                style={{
                  padding: "12px 24px",
                  background: "#1DB954",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontWeight: "600",
                }}
              >
                COPY
              </button>
            </div>
            <button
              onClick={closeSharePopup}
              className="w-100 mt-3"
              style={{
                padding: "12px",
                background: "transparent",
                border: "1px solid #444",
                borderRadius: "8px",
                color: "#ccc",
              }}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* PLAYLIST MODAL - NOW CLEARLY SHOWS SELECTION */}
      {playlistModal.open && playlistModal.song && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#1a1a1a",
              padding: "30px",
              borderRadius: "16px",
              width: "90%",
              maxWidth: "420px",
              color: "white",
              border: "1px solid #333",
            }}
          >
            <h4 className="text-center mb-2">Add to Playlist</h4>
            <p className="text-center text-muted mb-4">
              {playlistModal.song.title}
            </p>

            {!showCreateForm ? (
              <>
                <div
                  style={{
                    maxHeight: "260px",
                    overflowY: "auto",
                    marginBottom: "15px",
                  }}
                >
                  {playlists.length > 0 ? (
                    playlists.map((pl) => {
                      const checked = selectedPlaylists.has(pl._id);
                      return (
                        <div
                          key={pl._id}
                          onClick={() => togglePlaylistSelection(pl._id)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px",
                            borderRadius: "10px",
                            marginBottom: "8px",
                            background: checked
                              ? "rgba(29, 185, 84, 0.3)"
                              : "rgba(255,255,255,0.05)",
                            border:
                              "1px solid " + (checked ? "#1DB954" : "#444"),
                            cursor: "pointer",
                            transition: "0.2s",
                          }}
                        >
                          <div
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              border:
                                "2px solid " + (checked ? "#1DB954" : "#666"),
                              background: checked ? "#1DB954" : "transparent",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {checked && (
                              <i
                                className="fas fa-check"
                                style={{ color: "white", fontSize: "12px" }}
                              ></i>
                            )}
                          </div>
                          <img
                            src={
                              pl.playlistCoverUrl || "/img/live/songDefault.png"
                            }
                            alt=""
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: 6,
                              objectFit: "cover",
                            }}
                          />
                          <div>
                            <div style={{ fontWeight: 500 }}>{pl.name}</div>
                            {pl.description && (
                              <small style={{ color: "#aaa" }}>
                                {pl.description}
                              </small>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-4 text-muted">
                      No playlists yet
                    </div>
                  )}
                </div>

                <button
                  onClick={handleAddToSelectedPlaylists}
                  disabled={playlistLoading || selectedPlaylists.size === 0}
                  style={{
                    width: "100%",
                    padding: "14px",
                    background: "#1DB954",
                    border: "none",
                    borderRadius: "50px",
                    color: "white",
                    fontWeight: "600",
                    marginBottom: "10px",
                    opacity:
                      playlistLoading || selectedPlaylists.size === 0 ? 0.6 : 1,
                    cursor:
                      playlistLoading || selectedPlaylists.size === 0
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {playlistLoading
                    ? "Adding..."
                    : `Add to ${selectedPlaylists.size} Playlist${selectedPlaylists.size > 1 ? "s" : ""}`}
                </button>

                <button
                  onClick={() => setShowCreateForm(true)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "transparent",
                    border: "1px solid #444",
                    borderRadius: "50px",
                    color: "white",
                  }}
                >
                  Create New Playlist
                </button>
              </>
            ) : (
              /* CREATE FORM - unchanged */
              <div>
                <input
                  type="text"
                  placeholder="Playlist name *"
                  value={newPlaylist.name}
                  onChange={(e) =>
                    setNewPlaylist((p) => ({ ...p, name: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "#2a2a2a",
                    border: "1px solid #444",
                    borderRadius: "8px",
                    color: "white",
                    marginBottom: "12px",
                  }}
                />
                <textarea
                  placeholder="Description"
                  value={newPlaylist.description}
                  onChange={(e) =>
                    setNewPlaylist((p) => ({
                      ...p,
                      description: e.target.value,
                    }))
                  }
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "#2a2a2a",
                    border: "1px solid #444",
                    borderRadius: "8px",
                    color: "white",
                    marginBottom: "12px",
                    resize: "vertical",
                  }}
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{
                    width: "100%",
                    padding: "8px",
                    background: "#2a2a2a",
                    border: "1px solid #444",
                    borderRadius: "8px",
                    color: "white",
                    marginBottom: "15px",
                  }}
                />
                <div className="d-flex gap-2">
                  <button
                    onClick={createNewPlaylist}
                    disabled={playlistLoading}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: "#1DB954",
                      border: "none",
                      borderRadius: "50px",
                      color: "white",
                      fontWeight: "600",
                    }}
                  >
                    {playlistLoading ? "Creating..." : "Create"}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: "transparent",
                      border: "1px solid #444",
                      borderRadius: "50px",
                      color: "#ccc",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!showCreateForm && (
              <button
                onClick={closePlaylistModal}
                className="w-100 mt-3"
                style={{
                  padding: "12px",
                  background: "transparent",
                  border: "1px solid #444",
                  borderRadius: "50px",
                  color: "#ccc",
                }}
              >
                CLOSE
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Artist;
